/**
 * PayoutsPage.tsx
 *
 * The deposits Forward has made into the practice's bank account, and what went
 * into each one.
 *
 * ── Why the rows come from our own database ─────────────────────────────────
 * Forward's payouts API has no date filter, returns at most 50 per request, and
 * needs a further request per 50 transactions inside each payout. Reading it
 * live means paging through history and discarding most of it on every load.
 * So payouts are archived as they are seen, and this page reads that archive.
 * A payout that has settled can never change again, which is what makes the
 * copy trustworthy rather than merely fast.
 *
 * ── Expanding a row ────────────────────────────────────────────────────────
 * A payout's transactions are fetched the first time it is expanded and then
 * cached, so opening and closing the same row costs one request, not one per
 * click. The count on the row comes from the payout itself, so it is known
 * before anything is expanded.
 *
 * ── Exporting one payout ───────────────────────────────────────────────────
 * Each payout row carries its own Export, so one deposit can be reconciled
 * against the bank without the rest of the period in the same file. It sits on
 * the row rather than inside the expanded breakdown: a large payout's lines
 * push anything below them off the screen.
 *
 * ── Amounts ────────────────────────────────────────────────────────────────
 * Every amount is already in dollars; the API converts from the integer cents
 * Forward sends. Transaction amounts are SIGNED — fees and refunds are negative
 * and are what make the lines add up to the deposit.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Banknote, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Pagination } from '@/components/ui/Pagination';
import { Button } from '@/components/ui/Button';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import {
  getPayouts,
  getPayoutTransactions,
  listMerchants,
  type PayoutRow,
} from '@/services/payments.service';

const PER_PAGE = 50;

/** How many transactions load per request inside an expanded payout. */
const TXN_PAGE_SIZE = 50;

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search payout number or ID…' },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'Any status' },
  // Multi: an account can bank through several merchant accounts, and "what
  // landed in total last month" is the union of them. A single-choice filter
  // would make that several passes with no way to see the sum.
  { key: 'merchant', label: 'Merchant', type: 'select', allLabel: 'All merchants', multi: true },
  { key: 'daterange', label: 'Payout Date', type: 'daterange' },
];

/**
 * Forward's payout statuses. Labels are what a reader sees; the values are what
 * the API filters on.
 */
const STATUS_OPTIONS: FilterOption[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'staged', label: 'Staged' },
  { value: 'held', label: 'On hold' },
  { value: 'created', label: 'Created' },
  { value: 'returned', label: 'Returned' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
];

/**
 * Colour by outcome, not by name. A reader scanning the column needs to spot
 * the money that did not arrive; everything still in flight is neutral.
 */
const STATUS_TONES: Record<string, string> = {
  completed: 'bg-emerald-50 text-emerald-700',
  returned: 'bg-amber-50 text-amber-700',
  cancelled: 'bg-slate-100 text-slate-600',
  failed: 'bg-rose-50 text-rose-700',
};

function money(value: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
  }).format(value);
}

function shortDate(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value.replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

export default function PayoutsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [merchants, setMerchants] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { exporting, startExport, exportModal } = useAsyncExport({
    entityType: 'payouts',
    entityLabel: 'payouts',
  });

  // A second instance for exporting one payout, so its messages count the lines
  // in that deposit ("Exported 12 transactions.") instead of calling them payouts.
  const {
    exporting: exportingPayout,
    startExport: startPayoutExport,
    exportModal: payoutExportModal,
  } = useAsyncExport({
    entityType: 'payouts',
    entityLabel: 'transactions',
  });
  // Which payout the running export belongs to, so only its button spins.
  const [exportingPayoutId, setExportingPayoutId] = useState<string | null>(null);

  const exportPayout = useCallback((payoutId: string) => {
    setExportingPayoutId(payoutId);
    return startPayoutExport({ payoutId });
  }, [startPayoutExport]);

  const { data: merchantList = [] } = useQuery({
    queryKey: ['payment-merchants'],
    queryFn: listMerchants,
    staleTime: 5 * 60_000,
  });

  const filters = useMemo(() => ({
    search: search || undefined,
    status: status || undefined,
    merchantIds: merchants.length ? merchants.map(Number) : undefined,
    dateStart: dateFrom || undefined,
    dateEnd: dateTo || undefined,
  }), [search, status, merchants, dateFrom, dateTo]);

  const filterKey = JSON.stringify(filters);
  useEffect(() => { setPage(1); }, [filterKey]);

  // Collapse on any filter or page change: the expanded set is keyed by payout
  // id, and holding ids that are no longer on screen would re-open unrelated
  // rows the moment a filter brought them back.
  useEffect(() => { setExpanded(new Set()); }, [filterKey, page]);

  const { data, isFetching } = useQuery({
    queryKey: ['payouts', filterKey, page],
    queryFn: () => getPayouts({ ...filters, page, perPage: PER_PAGE }),
    // Rows stay on screen while the next page loads, so paging does not flash
    // an empty table.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totals = data?.meta.totals;

  const merchantOptions = useMemo<FilterOption[]>(
    () => merchantList.map((m) => ({ value: String(m.merchantId), label: m.friendlyTitle || 'Default' })),
    [merchantList],
  );

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({ status: STATUS_OPTIONS, merchant: merchantOptions }),
    [merchantOptions],
  );

  const sidebarValues = useMemo<Record<string, FilterValue>>(() => ({
    search: search ? [search] : [],
    status: status ? [status] : [],
    merchant: merchants,
    daterange: { from: dateFrom, to: dateTo },
  }), [search, status, merchants, dateFrom, dateTo]);

  const handleSidebarChange = useCallback((key: string, value: FilterValue) => {
    switch (key) {
      case 'search': if (Array.isArray(value)) setSearch(value[0] ?? ''); break;
      case 'status': if (Array.isArray(value)) setStatus(value[0] ?? ''); break;
      case 'merchant': if (Array.isArray(value)) setMerchants(value as string[]); break;
      case 'daterange':
        if (!Array.isArray(value) && 'from' in value) { setDateFrom(value.from || ''); setDateTo(value.to || ''); }
        break;
    }
  }, []);

  const handleSidebarClear = useCallback((key: string) => {
    switch (key) {
      case 'search': setSearch(''); break;
      case 'status': setStatus(''); break;
      case 'merchant': setMerchants([]); break;
      case 'daterange': setDateFrom(''); setDateTo(''); break;
    }
  }, []);

  const toggleRow = useCallback((payoutId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(payoutId)) next.delete(payoutId);
      else next.add(payoutId);
      return next;
    });
  }, []);

  const hasActiveFilters = !!search || !!status || merchants.length > 0 || !!dateFrom || !!dateTo;

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={FILTER_CONFIGS}
          values={sidebarValues}
          options={sidebarOptions}
          onChange={handleSidebarChange}
          onClear={handleSidebarClear}
          searchCommitMode="change"
          showAIAssistant={false}
          storageKey="payouts"
        />
      )}
    >
      <div className="flex h-full min-h-0 flex-col max-md:h-auto">
        <div className="shrink-0 space-y-6 p-6">
          <PageHeader
            variant="compact"
            title="Payouts"
            actions={(
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  leftIcon={<Download className="h-4 w-4" />}
                  onClick={() => startExport(filters)}
                  disabled={total === 0 || exporting}
                  loading={exporting}
                >
                  Export
                </Button>
                {exportModal}
                {payoutExportModal}
              </div>
            )}
          />

          <MetricCardGrid columns={4} data-testid="payouts-kpis">
            <StatCard label="Payouts" value={total} tone="blue" />
            <StatCard label="Total" value={money(totals?.amount ?? 0, 'USD')} tone="emerald" />
            <StatCard label="Fees" value={money(totals?.feeAmount ?? 0, 'USD')} tone="amber" />
            <StatCard label="Transactions" value={totals?.transactionCount ?? 0} tone="slate" />
          </MetricCardGrid>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-6 pb-6 max-md:overflow-visible">
          {rows.length === 0 && !isFetching ? (
            <div className="py-12 text-center">
              <Banknote className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">
                {hasActiveFilters ? 'No payouts match your filters.' : 'No payouts yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      {/* Spacer keeps the chevron out of the Date column so the
                          header labels stay above the values they name. */}
                      <th className="w-10 px-3 py-2" />
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Payout #</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Merchant</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-right">Fees</th>
                      <th className="px-3 py-2 text-right">Net</th>
                      <th className="px-3 py-2 text-right">Transactions</th>
                      {/* Spacer for the row's Export button. */}
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className={isFetching ? 'opacity-60 transition-opacity' : undefined}>
                    {rows.map((r) => (
                      <React.Fragment key={r.id}>
                        <tr className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => toggleRow(r.id)}
                              className="text-slate-400 hover:text-slate-600"
                              aria-expanded={expanded.has(r.id)}
                              aria-label={
                                expanded.has(r.id)
                                  ? `Hide transactions in payout ${r.payoutNumber}`
                                  : `Show transactions in payout ${r.payoutNumber}`
                              }
                            >
                              {expanded.has(r.id)
                                ? <ChevronDown className="h-4 w-4" />
                                : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-slate-600">{shortDate(r.date)}</td>
                          <td className="px-3 py-2 font-medium text-slate-900">{r.payoutNumber || '—'}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
                                STATUS_TONES[r.status] ?? 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              {r.statusLabel}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-600">{r.merchantName}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{money(r.amount, r.currency)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                            {money(r.feeAmount, r.currency)}
                          </td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums">
                            {money(r.netAmount, r.currency)}
                          </td>
                          {/* The count doubles as the expand control. The chevron
                              alone is a 16px glyph in an unlabelled column, and
                              readers were missing the breakdown entirely; the
                              number they want the detail behind is the obvious
                              thing to click.

                              It is a REDUNDANT target for the chevron, so it is
                              hidden from assistive tech and skipped in the tab
                              order — two controls with the same action in one row
                              would otherwise be announced twice and take two tab
                              stops each. The chevron stays the labelled one. */}
                          <td className="px-3 py-2 text-right tabular-nums">
                            {r.transactionCount > 0 ? (
                              <button
                                type="button"
                                onClick={() => toggleRow(r.id)}
                                className="font-medium text-blue-600 underline-offset-2 hover:underline"
                                aria-hidden="true"
                                tabIndex={-1}
                                data-testid={`payout-txn-count-${r.payoutNumber}`}
                              >
                                {r.transactionCount}
                              </button>
                            ) : (
                              <span className="text-slate-600">{r.transactionCount}</span>
                            )}
                          </td>
                          {/* Offered only once the breakdown is complete: a
                              spreadsheet of a partial deposit looks exactly like
                              one of a whole deposit. */}
                          <td className="px-3 py-2 text-right">
                            {r.detailsReady && r.transactionCount > 0 && (
                              <Button
                                variant="secondary"
                                size="xs"
                                leftIcon={<Download className="h-3 w-3" />}
                                onClick={() => exportPayout(r.id)}
                                disabled={exportingPayout}
                                loading={exportingPayout && exportingPayoutId === r.id}
                                aria-label={`Export payout ${r.payoutNumber}`}
                              >
                                Export
                              </Button>
                            )}
                          </td>
                        </tr>
                        {expanded.has(r.id) && (
                          <tr className="border-t border-slate-100 bg-slate-50/60">
                            <td colSpan={10} className="px-3 py-3">
                              <PayoutTransactions payout={r} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {total > PER_PAGE && (
                <Pagination
                  className="mt-4"
                  total={total}
                  page={page}
                  pageSize={PER_PAGE}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </ListPageLayout>
  );
}

/**
 * The transactions inside one expanded payout.
 *
 * Its own component so the query mounts with the panel: react-query then caches
 * per payout, and collapsing and re-opening a row costs nothing.
 */
function PayoutTransactions({ payout }: { payout: PayoutRow }) {
  const [loadedPages, setLoadedPages] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['payout-transactions', payout.id, loadedPages],
    queryFn: () => getPayoutTransactions(payout.id, { page: 1, perPage: TXN_PAGE_SIZE * loadedPages }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return <p className="py-2 text-sm text-slate-500">Loading transactions…</p>;
  }

  const rows = data?.data ?? [];
  const loadedAll = rows.length >= (data?.meta.total ?? 0);

  if (rows.length === 0) {
    return (
      <p className="py-2 text-sm text-slate-500">
        {payout.detailsReady
          ? 'This payout has no transactions.'
          : 'The breakdown for this payout is still being prepared. Check back shortly.'}
      </p>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto rounded border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-white text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Reference</th>
              <th className="px-3 py-2">Settlement</th>
              <th className="px-3 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="whitespace-nowrap px-3 py-2 text-slate-600">{shortDate(t.date)}</td>
                <td className="px-3 py-2 text-slate-700">{t.type}</td>
                <td className="px-3 py-2 text-slate-600">{t.description || '—'}</td>
                {/* The processor's own id for the charge this line came from —
                    the value staff match against when checking a deposit. */}
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-500">
                  {t.reference || '—'}
                </td>
                <td className="px-3 py-2 text-slate-500">{t.settlementStatus || '—'}</td>
                <td
                  className={`px-3 py-2 text-right tabular-nums ${
                    t.amount < 0 ? 'text-rose-600' : 'text-slate-900'
                  }`}
                >
                  {money(t.amount, t.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Only meaningful once the payout is settled — before that the archive is
          still filling in, and "not all of them yet" is a different statement
          from "there are more to load". */}
      {!payout.detailsReady && (
        <p className="mt-2 text-xs text-slate-500">
          The breakdown for this payout is still being prepared, so this list may be incomplete.
        </p>
      )}

      {!loadedAll && (
        <Button
          variant="secondary"
          className="mt-2"
          onClick={() => setLoadedPages((n) => n + 1)}
        >
          Show more
        </Button>
      )}
    </div>
  );
}
